import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiClock, FiCheckCircle, FiTruck, FiMap, FiNavigation } from 'react-icons/fi';
import { format } from 'date-fns';
import { alertService } from '../services/api';
import LoadingSpinner from '../components/common/LoadingSpinner';
import AlertDetailMap from '../components/map/AlertDetailMap';

const AlertDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [alert, setAlert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch alert data
  useEffect(() => {
    const fetchAlert = async () => {
      try {
        setLoading(true);
        
        // In a real app, we would fetch from API
        // const response = await alertService.getAlert(id);
        
        // For now, use dummy data
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Dummy alert data based on ID
        let dummyAlert;
        
        if (id === 'a1') {
          dummyAlert = {
            id: 'a1',
            type: 'geofence_entry',
            vehicle: { 
              id: 'v1', 
              name: 'Truck 101', 
              registration: 'ABC-1234',
              status: 'active' 
            },
            geofence: { 
              id: 'g1', 
              name: 'Downtown Zone',
              type: 'circle'
            },
            message: 'Vehicle entered Downtown Zone',
            severity: 'info',
            position: { lat: 40.7128, lng: -74.006 },
            acknowledged: false,
            createdAt: new Date().toISOString()
          };
        } else if (id === 'a2') {
          dummyAlert = {
            id: 'a2',
            type: 'geofence_exit',
            vehicle: { 
              id: 'v2', 
              name: 'Delivery Van 202', 
              registration: 'XYZ-5678',
              status: 'idle'
            },
            geofence: { 
              id: 'g3', 
              name: 'Warehouse District',
              type: 'circle'
            },
            message: 'Vehicle exited Warehouse District',
            severity: 'info',
            position: { lat: 40.7234, lng: -74.0123 },
            acknowledged: true,
            acknowledgedBy: { id: 'u1', name: 'Admin User' },
            acknowledgedAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(), // 30 minutes ago
            createdAt: new Date(Date.now() - 1000 * 60 * 60).toISOString() // 1 hour ago
          };
        } else if (id === 'a3') {
          dummyAlert = {
            id: 'a3',
            type: 'speeding',
            vehicle: { 
              id: 'v3', 
              name: 'Fleet Car 303', 
              registration: 'DEF-9012',
              status: 'active',
              speed: 45
            },
            message: 'Vehicle exceeding speed limit (45 mph in 35 mph zone)',
            severity: 'warning',
            position: { lat: 40.7118, lng: -74.009 },
            details: {
              speed: 45,
              speedLimit: 35
            },
            acknowledged: false,
            createdAt: new Date(Date.now() - 1000 * 60 * 15).toISOString() // 15 minutes ago
          };
        } else if (id === 'a4') {
          dummyAlert = {
            id: 'a4',
            type: 'idle_time',
            vehicle: { 
              id: 'v2', 
              name: 'Delivery Van 202', 
              registration: 'XYZ-5678',
              status: 'idle'
            },
            message: 'Vehicle idle for more than 30 minutes',
            severity: 'info',
            position: { lat: 40.7235, lng: -74.0125 },
            details: {
              idleTime: 32 // minutes
            },
            acknowledged: true,
            acknowledgedBy: { id: 'u1', name: 'Admin User' },
            acknowledgedAt: new Date(Date.now() - 1000 * 60 * 10).toISOString(), // 10 minutes ago
            createdAt: new Date(Date.now() - 1000 * 60 * 40).toISOString() // 40 minutes ago
          };
        } else if (id === 'a5') {
          dummyAlert = {
            id: 'a5',
            type: 'offline',
            vehicle: { 
              id: 'v4', 
              name: 'Pickup Truck 404', 
              registration: 'GHI-3456',
              status: 'offline'
            },
            message: 'Vehicle went offline',
            severity: 'error',
            position: { lat: 40.7145, lng: -74.0055 },
            details: {
              lastSeen: new Date(Date.now() - 1000 * 60 * 7).toISOString() // 7 minutes ago
            },
            acknowledged: false,
            createdAt: new Date(Date.now() - 1000 * 60 * 5).toISOString() // 5 minutes ago
          };
        } else {
          // If ID doesn't match any dummy data, simulate not found
          throw new Error('Alert not found');
        }

        setAlert(dummyAlert);
        setLoading(false);
      } catch (err) {
        setError(err.message || 'Failed to load alert');
        setLoading(false);
        console.error('Error fetching alert:', err);
      }
    };

    fetchAlert();
  }, [id]);

  // Handle acknowledge alert
  const handleAcknowledge = async () => {
    try {
      // In a real app, we would call API
      // await alertService.acknowledgeAlert(id);
      
      // For demo, just update local state
      setAlert(prev => ({
        ...prev,
        acknowledged: true,
        acknowledgedBy: { id: 'u1', name: 'Admin User' },
        acknowledgedAt: new Date().toISOString()
      }));
    } catch (err) {
      console.error('Error acknowledging alert:', err);
    }
  };

  // Get alert type icon and color
  const getAlertTypeInfo = (type) => {
    switch (type) {
      case 'geofence_entry':
        return { 
          icon: <FiMapPin />, 
          color: 'bg-blue-100 text-blue-800',
          label: 'Geofence Entry' 
        };
      case 'geofence_exit':
        return { 
          icon: <FiMapPin />, 
          color: 'bg-purple-100 text-purple-800',
          label: 'Geofence Exit' 
        };
      case 'speeding':
        return { 
          icon: <FiTruck />, 
          color: 'bg-orange-100 text-orange-800',
          label: 'Speeding' 
        };
      case 'idle_time':
        return { 
          icon: <FiClock />, 
          color: 'bg-yellow-100 text-yellow-800',
          label: 'Idle Time' 
        };
      case 'offline':
        return { 
          icon: <FiTruck />, 
          color: 'bg-red-100 text-red-800',
          label: 'Offline' 
        };
      default:
        return { 
          icon: <FiTruck />, 
          color: 'bg-gray-100 text-gray-800',
          label: 'Other' 
        };
    }
  };

  // Get severity color
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'text-red-600';
      case 'error':
        return 'text-red-500';
      case 'warning':
        return 'text-yellow-600';
      case 'info':
      default:
        return 'text-blue-500';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-10">
        <p className="text-ui-error">{error}</p>
        <Link to="/alerts" className="mt-4 btn btn-primary">
          Back to Alerts
        </Link>
      </div>
    );
  }

  if (!alert) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-500">Alert not found</p>
        <Link to="/alerts" className="mt-4 btn btn-primary">
          Back to Alerts
        </Link>
      </div>
    );
  }

  const { icon, color, label } = getAlertTypeInfo(alert.type);
  const severityColor = getSeverityColor(alert.severity);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center">
          <Link to="/alerts" className="mr-4">
            <FiArrowLeft className="text-gray-500 hover:text-primary" size={20} />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{alert.message}</h1>
            <div className="flex items-center mt-1">
              <span className={`mr-3 px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${color} items-center`}>
                <span className="mr-1">{icon}</span>
                {label}
              </span>
              <span className={`font-medium ${severityColor}`}>
                {alert.severity.charAt(0).toUpperCase() + alert.severity.slice(1)} Alert
              </span>
              <span className="mx-2 text-gray-400">•</span>
              <span className="text-gray-500">
                {format(new Date(alert.createdAt), 'MMM d, yyyy h:mm:ss a')}
              </span>
            </div>
          </div>
        </div>
        {!alert.acknowledged && (
          <button 
            onClick={handleAcknowledge}
            className="btn btn-primary flex items-center"
          >
            <FiCheckCircle className="mr-2" />
            Acknowledge Alert
          </button>
        )}
      </div>

      {/* Alert details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 card">
          <h2 className="text-lg font-semibold mb-4">Alert Location</h2>
          <div className="h-80">
            <AlertDetailMap 
              position={alert.position} 
              vehicle={alert.vehicle}
              geofence={alert.geofence}
              type={alert.type}
            />
          </div>
        </div>
        
        {/* Status and details */}
        <div className="card space-y-6">
          {/* Status */}
          <div>
            <h2 className="text-lg font-semibold mb-2">Status</h2>
            <div className="border-t border-gray-200 pt-2">
              {alert.acknowledged ? (
                <div className="flex items-center">
                  <FiCheckCircle className="text-ui-success mr-2" />
                  <div>
                    <p className="font-medium text-ui-success">Acknowledged</p>
                    <p className="text-sm text-gray-500">
                      by {alert.acknowledgedBy?.name || 'Unknown'} at{' '}
                      {format(new Date(alert.acknowledgedAt), 'MMM d, yyyy h:mm a')}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center text-yellow-600">
                  <FiClock className="mr-2" />
                  <p className="font-medium">Pending Acknowledgment</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Related vehicle */}
          {alert.vehicle && (
            <div>
              <h2 className="text-lg font-semibold mb-2">Vehicle</h2>
              <div className="border-t border-gray-200 pt-2">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Name:</span>
                    <Link to={`/vehicles/${alert.vehicle.id}`} className="font-medium text-primary">
                      {alert.vehicle.name}
                    </Link>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Registration:</span>
                    <span className="font-medium">{alert.vehicle.registration}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Status:</span>
                    <span className={`font-medium ${
                      alert.vehicle.status === 'active' ? 'text-ui-success' :
                      alert.vehicle.status === 'idle' ? 'text-yellow-600' :
                      alert.vehicle.status === 'stopped' ? 'text-red-500' :
                      'text-gray-500'
                    }`}>
                      {alert.vehicle.status.charAt(0).toUpperCase() + alert.vehicle.status.slice(1)}
                    </span>
                  </div>
                  {alert.vehicle.speed !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Speed:</span>
                      <span className="font-medium">{alert.vehicle.speed} mph</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {/* Related geofence */}
          {alert.geofence && (
            <div>
              <h2 className="text-lg font-semibold mb-2">Geofence</h2>
              <div className="border-t border-gray-200 pt-2">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-500">Name:</span>
                    <Link to={`/geofences/${alert.geofence.id}`} className="font-medium text-primary">
                      {alert.geofence.name}
                    </Link>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Type:</span>
                    <span className="font-medium">
                      {alert.geofence.type.charAt(0).toUpperCase() + alert.geofence.type.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Alert details */}
          {alert.details && (
            <div>
              <h2 className="text-lg font-semibold mb-2">Details</h2>
              <div className="border-t border-gray-200 pt-2">
                <div className="space-y-2">
                  {alert.details.speed !== undefined && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Measured Speed:</span>
                        <span className="font-medium">{alert.details.speed} mph</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-500">Speed Limit:</span>
                        <span className="font-medium">{alert.details.speedLimit} mph</span>
                      </div>
                    </>
                  )}
                  {alert.details.idleTime !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Idle Time:</span>
                      <span className="font-medium">{alert.details.idleTime} minutes</span>
                    </div>
                  )}
                  {alert.details.lastSeen !== undefined && (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Last Seen:</span>
                      <span className="font-medium">
                        {format(new Date(alert.details.lastSeen), 'h:mm:ss a')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertDetail;